/* purchase-viewport:start */
// Safari can anchor bottom-fixed elements to its layout viewport while its
// toolbar changes the visible viewport. Use the visible bottom as a top anchor.
export function bindPurchaseBarViewport(bar, view = window) {
    const viewport = view.visualViewport;
    if (!viewport)
        return () => { };
    let frame = null;
    const update = () => {
        frame = null;
        const bottom = viewport.offsetTop + viewport.height;
        if (!Number.isFinite(bottom) || bottom <= 0)
            return;
        bar.style.setProperty('--purchase-bar-bottom', `${bottom}px`);
        bar.toggleAttribute('data-viewport-anchored', true);
        const editing = view.document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
        bar.toggleAttribute('data-viewport-obscured', Boolean(editing) || viewport.scale > 1.05);
    };
    const schedule = () => { if (frame === null)
        frame = view.requestAnimationFrame(update); };
    const windowEvents = ['resize', 'orientationchange', 'pageshow', 'focusin', 'focusout'];
    viewport.addEventListener('resize', schedule, { passive: true });
    viewport.addEventListener('scroll', schedule, { passive: true });
    windowEvents.forEach(event => view.addEventListener(event, schedule, { passive: true }));
    update();
    return () => {
        if (frame !== null)
            view.cancelAnimationFrame(frame);
        viewport.removeEventListener('resize', schedule);
        viewport.removeEventListener('scroll', schedule);
        windowEvents.forEach(event => view.removeEventListener(event, schedule));
        bar.style.removeProperty('--purchase-bar-bottom');
        bar.removeAttribute('data-viewport-anchored');
        bar.removeAttribute('data-viewport-obscured');
    };
}
/* purchase-viewport:end */

const OFFER_STORAGE = 'little-echo.welcome.code.v1';
const SNOOZE_STORAGE = 'little-echo.welcome.until.v1';
const CART_SIGNAL = 'little-echo.shopify-cart.updated.v1';
const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
const rootPath = root => '/' + String(root || '/').replace(/^\/+|\/+$/g, '') + (String(root || '/').replace(/^\/+|\/+$/g, '') ? '/' : '');
const bottleCount = quantity => `${quantity} ${quantity === 1 ? 'bottle' : 'bottles'}`;
const money = (cents, currency = 'USD') => new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
const variantId = item => String(item.variant_id ?? item.id);
const ownItems = (cart, variants) => cart.items.filter(item => variants.some(variant => String(variant.id) === variantId(item)));
const savedCode = value => typeof value === 'string' && value.length <= 100 ? value.trim() : '';
const icon = (name, size) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${name === 'gift' ? '<path d="M3 8h18v5H3zM5 13v8h14v-8M12 8v13"/><path d="M12 8C2 8 6 0 10 4l2 4Zm0 0c10 0 6-8 2-4l-2 4Z"/>' : '<path d="M2 5h12v12H2zM14 9h4l4 5v3h-8"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="18" r="2"/>'}</svg>`;

export function bundleVariant(variants, quantity) {
  const variant = Number.isInteger(quantity) && quantity >= 1 && quantity <= 6 && variants.find(item => item.quantity === quantity);
  if (!variant) throw new RangeError('Choose between one and six bottles.');
  return variant;
}

export function selectedPack(raw, variants, fallback) {
  return variants.find(item => String(item.quantity) === raw)?.quantity ?? fallback ?? variants.find(item => item.quantity === 3 && item.available)?.quantity ?? variants.find(item => item.available)?.quantity ?? 3;
}

export function checkoutUrl(root, code) {
  const path = rootPath(root), discount = savedCode(code);
  return discount ? `${path}discount/${encodeURIComponent(discount)}?redirect=${encodeURIComponent(path + 'checkout')}` : path + 'checkout';
}

export function createCartClient(config, fetcher = globalThis.fetch.bind(globalThis), locks = globalThis.navigator?.locks) {
  const root = rootPath(config.cartRoot);
  let queue = Promise.resolve();
  async function request(path, body) {
    const response = await fetcher(root + path, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
    let data;
    try { data = await response.json(); } catch { throw new Error('Your bag could not be loaded. Please try again.'); }
    if (!response.ok) {
      const error = new Error(typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'Your bag could not be updated. Please try again.');
      error.status = response.status;
      throw error;
    }
    return data;
  }
  async function read() {
    const cart = await request('cart.js');
    if (!Array.isArray(cart.items) || !Number.isFinite(cart.total_price)) throw new Error('Your bag could not be loaded. Please try again.');
    return cart;
  }
  async function replace(quantity) {
    const target = quantity === null ? null : bundleVariant(config.variants, quantity);
    if (target && !target.available) throw new Error('This set is unavailable. Choose another size.');
    try {
      let cart = await read();
      if (target && !ownItems(cart, config.variants).some(item => variantId(item) === String(target.id))) {
        await request('cart/add.js', { items: [{ id: String(target.id), quantity: 1 }] });
        cart = await read();
      }
      const lines = ownItems(cart, config.variants);
      const keep = target && lines.find(item => variantId(item) === String(target.id));
      if (target && !keep) throw new Error('This set could not be added. Please try again.');
      const updates = {};
      for (const line of lines) {
        const next = line === keep ? 1 : 0;
        if (line.quantity !== next) updates[line.key] = next;
      }
      if (Object.keys(updates).length) cart = await request('cart/update.js', { updates });
      const final = ownItems(cart, config.variants);
      if (target ? final.length !== 1 || variantId(final[0]) !== String(target.id) || final[0].quantity !== 1 : final.length !== 0) {
        throw new Error('Your bag changed in another window. Choose your set again.');
      }
      return cart;
    } catch (error) {
      try { error.cart = await read(); } catch { /* The next action will reload the cart. */ }
      throw error;
    }
  }
  return {
    read,
    select(quantity) {
      const run = () => locks?.request ? locks.request('little-echo-shopify-cart', () => replace(quantity)) : replace(quantity);
      const result = queue.then(run);
      queue = result.catch(() => {});
      return result;
    },
  };
}

function cartConflict(cart, config) {
  const own = ownItems(cart, config.variants);
  return own.length > 1 || own.some(item => item.quantity !== 1);
}

export function cartMarkup(cart, config, state = {}) {
  const e = escapeHtml, fmt = amount => money(amount, cart.currency || 'USD');
  const disabled = state.busy ? ' disabled' : '';
  const error = state.error ? `<p class="cart-status" role="alert">${e(state.error)}</p>` : '';
  if (!cart.items.length) return `${error}<div class="cart-empty">${icon('gift', 48)}<h3>Your bag is empty.</h3><a href="${e(config.productUrl)}" class="button" data-cart-continue>Choose your bottles</a></div>`;
  const onlySets = ownItems(cart, config.variants).length === cart.items.length;
  const conflict = cartConflict(cart, config);
  const unavailable = ownItems(cart, config.variants).some(line => !config.variants.find(variant => String(variant.id) === variantId(line))?.available);
  const lines = cart.items.map(line => {
    const variant = config.variants.find(value => String(value.id) === variantId(line));
    const available = config.variants.filter(value => value.available).sort((a, b) => a.quantity - b.quantity);
    const smaller = variant && available.filter(value => value.quantity < variant.quantity).at(-1);
    const larger = variant && available.find(value => value.quantity > variant.quantity);
    const total = line.final_line_price ?? line.line_price;
    const image = variant ? config.productImage : line.featured_image?.url || line.image;
    const controls = variant ? `<div class="quantity-control"><button type="button" aria-label="Choose a smaller set" data-cart-set="${smaller?.quantity || ''}"${!smaller || state.busy ? ' disabled' : ''}>−</button><span aria-live="polite">${variant.quantity}</span><button type="button" aria-label="Choose a larger set" data-cart-set="${larger?.quantity || ''}"${!larger || state.busy ? ' disabled' : ''}>+</button></div><button type="button" class="text-button remove" data-cart-remove${disabled}>Remove</button>` : `<p>Quantity: ${line.quantity}</p>`;
    return `<div class="cart-item">${image ? `<img src="${e(image)}" alt="${e(variant ? 'Christmas tree voice bottle' : line.product_title)}" width="84" height="84">` : '<span></span>'}<div><h3>${e(variant ? config.productTitle : line.product_title || line.title)}</h3><p>${e(variant ? `Christmas tree · ${bottleCount(variant.quantity)}${line.quantity > 1 ? ` · ${line.quantity} sets` : ''}` : line.variant_title || '')}</p>${controls}</div><strong>${fmt(total)}</strong></div>${variant ? `<div class="bag-set-info"><span>${fmt(Math.round(total / (variant.quantity * line.quantity)))} per bottle</span><a href="${e(config.productUrl)}?pack=${variant.quantity}" data-cart-continue>Edit gift set</a></div>` : ''}`;
  }).join('');
  return `${error}${onlySets ? `<p class="cart-shipping">${icon('truck', 18)} Your set ships free in the U.S.</p>` : ''}${lines}${conflict ? `<p class="quiet" role="status">Choose one set to update your bag.</p><a class="text-button" href="${e(config.productUrl)}" data-cart-continue>Choose your set</a>` : ''}${unavailable ? '<p class="quiet" role="status">This set is currently unavailable. Choose another size or remove it.</p>' : ''}<div class="cart-totals"><p><span>List price</span><span>${fmt(cart.original_total_price)}</span></p>${cart.total_discount > 0 ? `<p><span>Discounts</span><span>−${fmt(cart.total_discount)}</span></p>` : ''}<p><span>Shipping</span><span>${onlySets ? 'Free in the U.S.' : 'Calculated at checkout'}</span></p><p class="cart-total"><span>Subtotal</span><strong data-testid="cart-subtotal">${fmt(cart.total_price)}</strong></p><p class="quiet">${e(cart.currency || 'USD')}. Taxes calculated at checkout.</p></div><form class="echo-checkout" action="${e(rootPath(config.cartRoot) + 'checkout')}" method="get"><details${state.code ? ' open' : ''}><summary>Welcome code</summary><label>10% code<input aria-label="10% code" name="discount" value="${e(state.code || '')}" autocomplete="off" autocapitalize="characters" maxlength="100"></label><small>First-order eligibility is checked at checkout.</small></details><button class="button checkout-button" type="submit"${state.busy || conflict || unavailable ? ' disabled' : ''}>${state.checkoutBusy ? 'Opening checkout…' : 'Checkout'}</button></form><a href="${e(config.productUrl)}" class="text-button continue" data-cart-continue>Continue browsing</a>`;
}

function setupGallery(config, quantity) {
  const photo = document.querySelector('.pdp-photo');
  const thumbs = document.querySelector('.pdp-thumbs');
  if (!photo || !thumbs) return;
  const choices = [
    ['fireside', 'By the fireplace', 'Christmas voice bottle beside the fire'],
    ['studio', 'Full bottle', 'The cork, red ribbon and glass foot of the Christmas voice bottle'],
    ['glass-and-star', 'Tree and ribbon detail', 'Gold star under the glass shoulder and red ribbon around the neck'],
    ['recorder-base', 'See the recorder', 'The speaker openings and controls on the underside of the bottle'],
    ['give-a-little-echo', 'Giving the bottle', 'Two hands passing a Christmas voice bottle'],
  ];
  if (quantity === 3) choices.unshift(['three-bottles', 'Three-bottle set', 'A set of three Christmas voice bottles']);
  if (quantity === 6) choices.unshift(['six-bottles', 'Six-bottle set', 'Six Christmas voice bottles in a row']);
  const src = name => config.images?.['/images/product/' + name + '.webp'];
  const available = choices.filter(choice => src(choice[0]));
  if (!available.length) return;
  function show(index) {
    const [name, , alt] = available[index];
    const img = photo.querySelector('img');
    if (img) {
      img.src = src(name);
      img.removeAttribute('srcset');
      img.alt = alt;
    }
    photo.classList.toggle('pdp-photo--detail', name === 'three-bottles' || name === 'six-bottles');
    thumbs.querySelectorAll('button').forEach((button, current) => button.setAttribute('aria-pressed', String(current === index)));
  }
  thumbs.replaceChildren(...available.map(([name, label], index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('aria-label', label);
    const img = document.createElement('img');
    img.src = src(name + (name === 'give-a-little-echo' ? '-480' : '-160')) || src(name);
    img.alt = '';
    img.width = 72;
    img.height = 72;
    button.append(img);
    button.addEventListener('click', () => show(index));
    return button;
  }));
  show(0);
}

function storageRead(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

function storageWrite(key, value) {
  try { if (value) localStorage.setItem(key, value); else localStorage.removeItem(key); } catch { /* Storage is optional. */ }
}

function setupWelcome(config, onCode) {
  const dialog = document.querySelector('.welcome-dialog');
  if (!dialog) return;
  let code = savedCode(storageRead(OFFER_STORAGE));
  let until = Number(storageRead(SNOOZE_STORAGE)) || 0;
  const heading = dialog.querySelector('#welcome-title');
  const success = dialog.querySelector('[data-welcome-success]');
  function showCode(value) {
    code = value;
    const join = dialog.querySelector('[data-welcome-join]');
    const done = dialog.querySelector('[data-welcome-done]');
    if (join) join.hidden = Boolean(code);
    if (done) done.hidden = !code;
    if (heading && code) heading.textContent = 'Your 10% code.';
    dialog.querySelectorAll('.welcome-code').forEach(output => { output.textContent = code; });
  }
  function open() {
    if (!dialog.open && !document.querySelector('dialog[open]')) {
      dialog.showModal();
      heading?.focus();
    }
  }
  document.querySelectorAll('.welcome-trigger').forEach(button => {
    button.disabled = false;
    button.addEventListener('click', open);
  });
  window.addEventListener('little-echo:open-welcome', open);
  dialog.addEventListener('click', event => {
    if (event.target === dialog || event.target.closest('.welcome-close, [data-welcome-close]')) dialog.close();
  });
  dialog.addEventListener('close', () => {
    until = Date.now() + (code ? 90 : 7) * 86400000;
    storageWrite(SNOOZE_STORAGE, String(until));
  });
  if (success && savedCode(config.welcomeCode)) {
    code = savedCode(config.welcomeCode);
    storageWrite(OFFER_STORAGE, code);
    onCode(code);
    window.dispatchEvent(new Event(OFFER_STORAGE));
  }
  const hasError = Boolean(dialog.querySelector('[data-welcome-error]'));
  if (!hasError) showCode(code);
  if (success || hasError) open();
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const productPath = new URL(config.productUrl, window.location.origin).pathname.replace(/\/$/, '');
  const homePath = rootPath(config.cartRoot).replace(/\/$/, '') || '/';
  const start = performance.now();
  function maybeOpen() {
    if (performance.now() - start < 20000 || Date.now() < Math.max(until, Number(storageRead(SNOOZE_STORAGE)) || 0) || document.hidden || document.querySelector('dialog[open], audio[data-playing=true]') || document.activeElement?.matches('input,textarea,select')) return;
    if ([...document.querySelectorAll('audio,video')].some(media => !media.muted && !media.paused)) return;
    const distance = Math.max(0, Math.min(320, (document.documentElement.scrollHeight - window.innerHeight) * 0.2));
    if (window.scrollY >= distance) open();
  }
  if (path === homePath || path === productPath) {
    window.setTimeout(maybeOpen, 20050);
    window.addEventListener('scroll', maybeOpen, { passive: true });
  }
}

function startCommerce(config) {
  if (!Array.isArray(config.variants) || !config.variants.length) return;
  const client = createCartClient(config);
  const state = { cart: null, busy: false, checkoutBusy: false, error: '', code: savedCode(storageRead(OFFER_STORAGE)) };
  const dialog = document.querySelector('.cart-dialog');
  const buy = document.querySelector('.pdp-buy');
  let version = 0;
  let explicitSelection = new URLSearchParams(window.location.search).has('pack') || new URLSearchParams(window.location.search).has('variant');
  function querySelection() {
    const params = new URLSearchParams(window.location.search);
    const fromVariant = config.variants.find(variant => String(variant.id) === params.get('variant'))?.quantity;
    return selectedPack(params.get('pack'), config.variants, fromVariant);
  }
  let quantity = querySelection();
  let sticky = document.querySelector('.pdp-sticky');
  if (buy && !sticky) {
    sticky = document.createElement('div');
    sticky.className = 'pdp-sticky';
    sticky.setAttribute('aria-label', 'Selected gift set');
    sticky.innerHTML = '<strong></strong><button class="button" type="button" data-sticky-buy></button>';
    document.body.append(sticky);
  }
  if (sticky) {
    sticky.hidden = true;
    bindPurchaseBarViewport(sticky);
  }
  if (buy && sticky && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => { sticky.hidden = entry.isIntersecting; }).observe(buy);
  }
  function chosenInCart() {
    if (!state.cart || cartConflict(state.cart, config)) return null;
    const line = ownItems(state.cart, config.variants)[0];
    return line ? config.variants.find(variant => String(variant.id) === variantId(line))?.quantity : null;
  }
  function renderProduct() {
    const variant = config.variants.find(value => value.quantity === quantity);
    const current = chosenInCart();
    const action = !variant?.available ? 'Set unavailable' : state.busy ? 'Updating bag…' : current === quantity ? 'View gift bag' : ownItems(state.cart || { items: [] }, config.variants).length ? 'Update gift bag' : 'Add to gift bag';
    document.querySelectorAll('.pack-card[data-pack]').forEach(button => {
      const choice = config.variants.find(value => value.quantity === Number(button.dataset.pack));
      button.setAttribute('aria-pressed', String(choice?.quantity === quantity));
      button.disabled = !choice?.available || state.busy;
    });
    if (buy) {
      const textNode = [...buy.childNodes].find(node => node.nodeType === 3);
      if (textNode) textNode.textContent = action;
      else buy.prepend(document.createTextNode(action));
      const price = buy.querySelector('span');
      if (price && variant) {
        const text = [...price.childNodes].find(node => node.nodeType === 3);
        if (text) text.textContent = money(variant.salePrice) + ' ';
        else price.prepend(document.createTextNode(money(variant.salePrice) + ' '));
      }
      buy.disabled = !variant?.available || state.busy;
      buy.setAttribute('aria-busy', String(state.busy));
    }
    if (sticky) {
      const label = sticky.querySelector('strong');
      const button = sticky.querySelector('button');
      if (label) label.textContent = bottleCount(quantity);
      if (button) { button.textContent = action; button.disabled = !variant?.available || state.busy; }
    }
    document.querySelectorAll('[data-product-form] [name="id"], #echo-product-form [name="id"]').forEach(input => { if (variant) input.value = String(variant.id); });
    let status = document.querySelector('[data-product-status]');
    if (!status && buy) {
      status = document.createElement('p');
      status.className = 'pdp-status';
      status.dataset.productStatus = '';
      status.setAttribute('role', 'status');
      buy.after(status);
    }
    if (status) {
      status.textContent = state.error || (!variant?.available ? 'Choose an available set.' : '');
      status.hidden = !status.textContent;
    }
  }
  function render() {
    document.querySelectorAll('[data-cart-content]').forEach(container => {
      const active = container.contains(document.activeElement) ? document.activeElement : null;
      const focusSelector = active?.matches('[data-cart-set]') ? `[data-cart-set="${active.dataset.cartSet}"]` : active?.matches('[data-cart-remove]') ? '[data-cart-remove]' : active?.matches('.checkout-button') ? '.checkout-button' : null;
      container.innerHTML = state.cart ? cartMarkup(state.cart, config, state) : `<p role="${state.error ? 'alert' : 'status'}">${escapeHtml(state.error || 'Loading your bag…')}</p>${state.error ? '<button type="button" class="text-button" data-cart-retry>Try again</button>' : ''}`;
      if (focusSelector) container.querySelector(focusSelector)?.focus();
      container.setAttribute('aria-busy', String(state.busy));
    });
    if (state.cart) {
      const count = state.cart.items.reduce((sum, line) => sum + (config.variants.find(variant => String(variant.id) === variantId(line))?.quantity || 1) * line.quantity, 0);
      document.querySelectorAll('[data-cart-count], .bag-count').forEach(node => { node.textContent = String(count); node.hidden = count === 0; });
      document.querySelectorAll('.bag-button, [data-cart-open]').forEach(button => button.setAttribute('aria-label', count ? `Open bag, ${count} ${ownItems(state.cart, config.variants).length === state.cart.items.length ? 'bottles' : 'items'}` : 'Open bag'));
    }
    renderProduct();
  }
  function setSelected(next, updateUrl = false) {
    quantity = next;
    if (updateUrl) {
      explicitSelection = true;
      const url = new URL(window.location.href);
      url.searchParams.set('pack', String(next));
      url.searchParams.set('variant', String(bundleVariant(config.variants, next).id));
      history.replaceState(history.state, '', url);
    }
    setupGallery(config, quantity);
    renderProduct();
  }
  async function refresh() {
    if (state.busy) return;
    const currentVersion = ++version;
    try {
      const cart = await client.read();
      if (version !== currentVersion) return;
      state.cart = cart;
      state.error = '';
      if (!explicitSelection && chosenInCart()) setSelected(chosenInCart());
      render();
    } catch (error) {
      if (version !== currentVersion) return;
      state.error = error instanceof TypeError ? 'Check your connection and try again.' : error.message;
      render();
    }
  }
  function openCart() {
    document.querySelector('.welcome-dialog[open]')?.close();
    if (dialog && !dialog.open) dialog.showModal();
    refresh();
  }
  async function changeSet(next, open = false) {
    if (state.busy) return;
    version++;
    state.busy = true;
    state.error = '';
    render();
    try {
      state.cart = await client.select(next);
      if (next !== null) setSelected(next, Boolean(buy));
      storageWrite(CART_SIGNAL, `${Date.now()}-${Math.random()}`);
      if (open && dialog && !dialog.open) dialog.showModal();
    } catch (error) {
      if (error.cart) state.cart = error.cart;
      else state.cart = null;
      state.error = error instanceof TypeError ? 'Check your connection and try again.' : error.message;
    } finally {
      state.busy = false;
      render();
    }
  }
  function purchase() {
    if (chosenInCart() === quantity) openCart();
    else changeSet(quantity, true);
  }
  document.addEventListener('click', event => {
    const target = event.target.closest('button, a');
    if (!target || target.disabled) return;
    if (target.matches('.pack-card[data-pack]')) { event.preventDefault(); setSelected(Number(target.dataset.pack), true); }
    else if (target.matches('.pdp-buy, .pdp-sticky button')) { event.preventDefault(); purchase(); }
    else if (target.matches('.bag-button, [data-cart-open]')) { event.preventDefault(); openCart(); }
    else if (target.matches('[data-cart-close], .cart-heading button')) { event.preventDefault(); dialog?.close(); }
    else if (target.matches('[data-cart-set]')) { event.preventDefault(); changeSet(Number(target.dataset.cartSet)); }
    else if (target.matches('[data-cart-remove]')) { event.preventDefault(); changeSet(null); }
    else if (target.matches('[data-cart-retry]')) { event.preventDefault(); refresh(); }
    else if (target.matches('[data-cart-continue]')) {
      dialog?.close();
      if (buy) {
        const url = new URL(target.href);
        if (url.pathname === window.location.pathname) {
          event.preventDefault();
          const raw = url.searchParams.get('pack');
          if (raw) setSelected(selectedPack(raw, config.variants), true);
          document.querySelector('#gift-sets')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  });
  dialog?.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
  document.addEventListener('input', event => {
    if (!event.target.matches('.echo-checkout input[name="discount"]')) return;
    state.code = savedCode(event.target.value);
    storageWrite(OFFER_STORAGE, state.code);
    document.querySelectorAll('.echo-checkout input[name="discount"]').forEach(input => { if (input !== event.target) input.value = state.code; });
  });
  document.addEventListener('submit', async event => {
    if (event.target.matches('[data-product-form], #echo-product-form')) { event.preventDefault(); purchase(); return; }
    if (!event.target.matches('.echo-checkout')) return;
    event.preventDefault();
    if (state.busy) return;
    state.code = savedCode(new FormData(event.target).get('discount'));
    storageWrite(OFFER_STORAGE, state.code);
    state.busy = true;
    state.checkoutBusy = true;
    state.error = '';
    version++;
    render();
    try {
      state.cart = await client.read();
      if (!state.cart.items.length) throw new Error('Your bag is empty. Choose a set first.');
      if (cartConflict(state.cart, config)) throw new Error('Your bag changed. Choose one set before checkout.');
      window.location.assign(checkoutUrl(config.cartRoot, state.code));
    } catch (error) {
      state.error = error instanceof TypeError ? 'Check your connection and try again.' : error.message;
      state.busy = false;
      state.checkoutBusy = false;
      render();
    }
  });
  function codeChanged() { state.code = savedCode(storageRead(OFFER_STORAGE)); render(); }
  window.addEventListener(OFFER_STORAGE, codeChanged);
  window.addEventListener('storage', event => {
    if (event.key === OFFER_STORAGE || event.key === null) codeChanged();
    if (event.key === CART_SIGNAL || event.key === null) refresh();
  });
  window.addEventListener('focus', refresh);
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    state.busy = false;
    state.checkoutBusy = false;
    render();
    refresh();
  });
  window.addEventListener('popstate', () => { explicitSelection = true; setSelected(querySelection()); });
  setupWelcome(config, code => { state.code = code; render(); });
  setupGallery(config, quantity);
  render();
  refresh();
}

if (typeof window !== 'undefined' && window.LE) startCommerce(window.LE);
